import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { BOMAPI } from "../services/api";
import React from "react";

type BOQChildItem = {
  work: string;
  quantity: number;
  unitName: string;
  activitySpec: string;
};

type BOQGroup = {
  treeNodeNo: number;
  parentNode: number;
  workTreeName: string;
  items: BOQChildItem[];
};

const BOMPage = () => {
  const { wbs } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState<BOQGroup[]>([]);
  const [loading, setLoading] = useState(true);

  // 🔥 track expanded nodes
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!wbs) return;

        const res = await BOMAPI.getByWBS(Number(wbs));
        setData(res);

        // expand root by default
        const initialExpand: any = {};
        res.forEach((x: BOQGroup) => {
          if (!x.parentNode) initialExpand[x.treeNodeNo] = true;
        });
        setExpanded(initialExpand);

      } catch (err) {
        console.error(err);
        alert("Failed to load BOM");
      }

      setLoading(false);
    };

    fetchData();
  }, [wbs]);

  // 🔥 toggle expand/collapse
  const toggle = (id: number) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // 🔥 BUILD TREE
  const buildHierarchy = () => {
    const map: any = {};

    data.forEach((g) => {
      map[g.treeNodeNo] = { ...g, children: [] };
    });

    const tree: any[] = [];

    data.forEach((g) => {
      if (g.parentNode && map[g.parentNode]) {
        map[g.parentNode].children.push(map[g.treeNodeNo]);
      } else {
        tree.push(map[g.treeNodeNo]);
      }
    });

    return tree;
  };

  const treeData = buildHierarchy();

  // 🔥 RENDER TREE
  const renderTree = (nodes: any[], level = 0) => {
    return nodes.map((node, i) => {
      const isOpen = expanded[node.treeNodeNo];

      return (
        <React.Fragment key={i}>

          {/* 🔷 SECTION HEADER WITH TOGGLE */}
          <tr
            className="bg-gray-200 font-bold cursor-pointer hover:bg-gray-300"
            onClick={() => toggle(node.treeNodeNo)}
          >
            <td
              colSpan={4}
              className="p-2 flex items-center gap-2"
              style={{ paddingLeft: `${level * 20}px` }}
            >
              {/* + / - icon */}
              <span className="w-4 inline-block">
                {isOpen ? "−" : "+"}
              </span>

              {node.workTreeName}
            </td>
          </tr>

          {/* 🔸 SHOW CONTENT ONLY IF EXPANDED */}
          {isOpen && (
            <>
              {/* ITEMS */}
              {node.items.map((item: any, j: number) => (
                <tr key={j} className="border-b hover:bg-gray-100">

                  <td
                    className="p-2 font-semibold"
                    style={{ paddingLeft: `${(level + 1) * 20}px` }}
                  >
                    {item.work}
                  </td>

                  <td className="p-2 text-right">
                    {item.quantity
                      ? Number(item.quantity).toFixed(2)
                      : "-"}
                  </td>

                  <td className="p-2">{item.unitName}</td>

                  <td className="p-2 text-xs">
                    {(item.activitySpec || "")
                      .split(";")
                      .filter((s: string) => s.trim())
                      .map((s: string, k: number) => (
                        <div key={k}>• {s.trim()}</div>
                      ))}
                  </td>

                </tr>
              ))}

              {/* CHILD NODES */}
              {node.children.length > 0 &&
                renderTree(node.children, level + 1)}
            </>
          )}

        </React.Fragment>
      );
    });
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">

      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold">
          BOQ Report (WBS: {wbs})
        </h1>

        <button
          onClick={() => navigate(-1)}
          className="bg-gray-600 text-white px-3 py-1 rounded"
        >
          Back
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="bg-white shadow rounded overflow-auto">

          <table className="min-w-full text-sm border">

            {/* HEADER */}
            <thead className="bg-gray-400 text-white">
              <tr>
                <th className="p-2 text-left">Item</th>
                <th className="p-2 text-right">Quantity</th>
                <th className="p-2 text-left">Unit</th>
                <th className="p-2 text-left">Specifications</th>
              </tr>
            </thead>

            <tbody>
              {renderTree(treeData)}
            </tbody>

          </table>

        </div>
      )}
    </div>
  );
};

export default BOMPage;